import type { ComponentProps, InputHTMLAttributes, MouseEvent, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { useId } from "react";
import { cn } from "@/lib/utils";

const control =
  "min-w-0 w-full rounded-control border border-control-border bg-surface px-3.5 py-2.5 text-[15px] text-text placeholder:text-text-tertiary focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-60";

export function Field({ label, hint, error, children, className }: { label?: string; hint?: string; error?: string; children: ReactNode; className?: string }) {
  return (
    <label className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      {label && <span className="text-[13px] font-semibold text-text-secondary">{label}</span>}
      {children}
      {error ? <span className="text-xs text-danger">{error}</span> : hint ? <span className="text-xs text-text-tertiary">{hint}</span> : null}
    </label>
  );
}

const PICKER_TYPES = new Set(["date", "time", "datetime-local", "month", "week"]);

export function Input({ className, onClick, ...rest }: ComponentProps<"input">) {
  // Date and time fields open their picker from anywhere in the field, not only from the small calendar icon.
  const openPicker = (e: MouseEvent<HTMLInputElement>) => {
    onClick?.(e);
    const el = e.currentTarget;
    if (!e.defaultPrevented && PICKER_TYPES.has(el.type) && !el.disabled && !el.readOnly) {
      try { el.showPicker?.(); } catch { /* not allowed here (e.g. inside a cross-origin frame): the icon still works */ }
    }
  };
  return <input className={cn(control, className)} onClick={openPicker} {...rest} />;
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(control, "min-h-24 resize-y", className)} {...rest} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    // Native <select> (the OS picker on phones is the best one there is); only the closed control is ours.
    <select className={cn(control, "select-chevron cursor-pointer appearance-none truncate pr-9 transition-colors hover:border-accent disabled:cursor-not-allowed", className)} {...rest}>
      {children}
    </select>
  );
}

/** Money input: numeric keyboard on phones, ISO code prefix like iOS `CurrencyAmountField`. */
export function MoneyInput({ currency, value, onChange, className, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> & { currency: string; value: number; onChange: (n: number) => void }) {
  return (
    <div className={cn("flex items-center rounded-control border border-control-border bg-surface focus-within:border-accent focus-within:ring-2 focus-within:ring-accent", className)}>
      <span className="pl-3.5 text-sm font-semibold text-text-tertiary">{currency}</span>
      <input
        type="number"
        inputMode="decimal"
        min={0}
        step="0.01"
        value={Number.isFinite(value) && value !== 0 ? value : ""}
        placeholder="0"
        onChange={(e) => onChange(e.target.value === "" ? 0 : Math.max(0, Number(e.target.value)))}
        // Scrolling the page over a focused number field must never change the amount.
        onWheel={(e) => { if (document.activeElement === e.currentTarget) e.currentTarget.blur(); }}
        className="min-w-0 w-full bg-transparent px-3 py-2.5 text-[15px] text-text tabular placeholder:text-text-tertiary focus:outline-none"
        {...rest}
      />
    </div>
  );
}

/**
 * iOS-style toggle. The label is a real <label> for the switch (click either), an optional hint is
 * wired through aria-describedby, and the knob is anchored to the track's left edge — never to the
 * button's centred text baseline, which is what used to push it out of the track.
 */
export function Switch({ checked, onChange, label, hint, disabled, className }: {
  checked: boolean; onChange: (v: boolean) => void; label?: string; hint?: string; disabled?: boolean; className?: string;
}) {
  const id = useId();
  const hintId = useId();
  return (
    <div className={cn("flex items-center justify-between gap-3 py-1", disabled && "opacity-60", className)}>
      {(label || hint) && (
        <span className="min-w-0 flex-1">
          {label && <label htmlFor={id} className={cn("block text-[15px] text-text", !disabled && "cursor-pointer")}>{label}</label>}
          {hint && <span id={hintId} className="block text-xs text-text-tertiary">{hint}</span>}
        </span>
      )}
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-describedby={hint ? hintId : undefined}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={cn(
          "relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-200 motion-reduce:transition-none disabled:cursor-not-allowed",
          checked ? "bg-accent" : "bg-text/15",
        )}
      >
        <span
          aria-hidden
          className={cn("absolute left-0.5 top-0.5 h-6 w-6 rounded-full bg-white shadow-sm transition-transform duration-200 motion-reduce:transition-none", checked ? "translate-x-5" : "translate-x-0")}
        />
      </button>
    </div>
  );
}

export function Checkbox({ checked, onChange, disabled, className, label }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; className?: string; label?: string }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      className={cn(
        "grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 transition-colors motion-reduce:transition-none disabled:cursor-not-allowed disabled:opacity-50",
        checked ? "border-success bg-success text-on-pastel" : "border-accent bg-transparent",
        className,
      )}
    >
      {checked && (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="m5 12 5 5L20 7" /></svg>
      )}
    </button>
  );
}
